import {
  Component,
  ElementRef,
  Inject,
  OnInit,
  ViewChild,
  AfterViewInit,
  OnDestroy,
} from '@angular/core';

import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { translate } from '@jsverse/transloco';

import {
  injectComponentTelemetryService,
  EnableComponentTelemetry,
  EnableClassTelemetry,
} from 'app/services/otel.service';

export const enum SimpleDialogButtonColor {
  red = 'error',
  green = 'success',
  blue = 'orca',
  white = 'orca',
  orange = 'warning',
}

interface SimpleDialogButtonOptions<T = any> {
  title: string;
  handler: (ref: MatDialogRef<SimpleDialogComponent>, v?: T) => void;
  color?: SimpleDialogButtonColor;
}

export interface SimpleDialogOptions<T = any> {
  title: string;
  titleTooltip?: string;
  content: string;
  extraContent?: string;
  cancelTitle?: string;
  disableCancel?: boolean;
  confirmTitle?: string;
  confirmColor?: SimpleDialogButtonColor;
  target?: T;
  buttons?: SimpleDialogButtonOptions<T>[];
  confirmButtonDisabled?: boolean;
  confirmButtonDisabledTooltip?: string;
}

@EnableClassTelemetry()
@Component({
  selector: 'app-simple-dialog',
  templateUrl: './simple-dialog.component.html',
  styleUrls: ['./simple-dialog.component.scss'],
  standalone: false,
})
export class SimpleDialogComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('actionButtons') actionButtons: ElementRef;
  @EnableComponentTelemetry() otelService = injectComponentTelemetryService();

  resizeObserver: ResizeObserver;
  constructor(
    private dialogRef: MatDialogRef<SimpleDialogComponent>,
    @Inject(MAT_DIALOG_DATA) private options: SimpleDialogOptions
  ) {}

  ngOnInit() {
    this.resizeObserver = new ResizeObserver(() => {
      if (
        this.actionButtons.nativeElement.scrollWidth > this.actionButtons.nativeElement.clientWidth
      ) {
        this.actionButtons.nativeElement.classList.add('simple-dialog__actions-stacked');
      }
    });
  }

  ngAfterViewInit(): void {
    for (const child of this.actionButtons.nativeElement.children) {
      this.resizeObserver.observe(child);
    }
  }

  ngOnDestroy() {
    this.resizeObserver.disconnect();
  }

  public onButtonClick(button: SimpleDialogButtonOptions<any>): void {
    button.handler(this.dialogRef, this.options.target);
  }

  public get title(): string {
    return this.options.title;
  }

  public get titleTooltip(): string {
    return this.options.titleTooltip;
  }

  public get content(): string {
    return this.options.content;
  }

  public get extraContent(): string | void {
    return this.options.extraContent;
  }

  public get cancelTitle(): string {
    if (!this.options.confirmTitle) {
      return this.options.cancelTitle || translate('simple-dialog.close');
    }
    return this.options.cancelTitle || translate('simple-dialog.cancel');
  }

  public get disableCancel(): boolean {
    if (!this.options.disableCancel) {
      return false;
    }
    return this.options.disableCancel;
  }

  public get confirmTitle(): string | void {
    return this.options.confirmTitle;
  }

  public get confirmColor(): string | void {
    return this.options.confirmColor;
  }

  public get buttons(): SimpleDialogButtonOptions[] {
    return this.options.buttons || [];
  }

  cancel(): void {
    this.dialogRef.close(false);
  }

  confirm(): void {
    this.dialogRef.close(true);
  }
}
